import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { colors } from '../theme';
import { formatAddress } from '../addressUtils';
import RmaPdfActions from './RmaPdfActions.jsx';
import fieldConstants from '../constants/rmaFieldConstants.json';
import { locationLabel } from '../utils/rmaLocation';

const CLOSURE_REASON_LABELS = (fieldConstants.prematureClosureReasons || []).reduce((acc, r) => {
  acc[r.value] = r.label;
  return acc;
}, {});

function shortDate(val) {
  if (!val) return null;
  return new Date(val).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

function prettifyStage(stage) {
  if (!stage) return null;
  return String(stage).replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function Row({ label, value }) {
  if (!value) return null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.1rem' }}>
      <span style={{ fontSize: '0.75rem', fontWeight: 600, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        {label}
      </span>
      <span style={{ fontSize: '0.88rem', color: colors.textBody }}>{value}</span>
    </div>
  );
}

export default function ArchivedRmaCard({ rma, showPdfLink = true, pdfLocale = 'en', onChangePdfLocale, highlighted = false }) {
  const [open, setOpen] = useState(highlighted);
  if (!rma) return null;

  const issueType = rma.issueType ? rma.issueType.charAt(0).toUpperCase() + rma.issueType.slice(1) : null;
  const cancellation = rma.cancellation || {};
  const isCancelled = rma.status === 'cancelled' || !!cancellation.reason;
  const closedDate = shortDate(rma.archivedAt || cancellation.at || rma.submittedAt);
  const statusBadge = isCancelled
    ? { label: 'CLOSED EARLY', bg: '#fb923c' }
    : { label: 'ARCHIVED', bg: colors.accent };

  return (
    <article
      data-testid="archived-rma-card"
      style={{
        borderRadius: '10px',
        border: `1px solid ${highlighted ? colors.accent : colors.panelBorder}`,
        background: 'rgba(255,255,255,0.02)',
        overflow: 'hidden',
        outline: highlighted ? `2px solid ${colors.accent}` : 'none',
        outlineOffset: '2px'
      }}
    >
      {/* Collapsed row — always visible */}
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        style={{
          width: '100%',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          padding: '0.7rem 1rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem',
          textAlign: 'left'
        }}
      >
        <span style={{ fontFamily: "'Barlow Condensed','Barlow',sans-serif", fontWeight: 700, fontSize: '1rem', color: colors.textStrong, letterSpacing: '0.04em', flexShrink: 0 }}>
          {rma.rmaId || 'RMA'}
        </span>
        <span style={{ fontSize: '0.8rem', color: colors.textMuted, flexShrink: 0 }}>
          {rma.serialNumber}
        </span>
        {issueType && (
          <span style={{
            fontSize: '0.72rem', fontWeight: 600, color: colors.accent,
            border: `1px solid ${colors.accent}`, borderRadius: '999px',
            padding: '0.1rem 0.5rem', flexShrink: 0
          }}>
            {issueType}
          </span>
        )}
        <span style={{ flex: 1 }} />
        {closedDate && (
          <span style={{ fontSize: '0.78rem', color: colors.textMuted, flexShrink: 0 }}>{closedDate}</span>
        )}
        <span
          aria-label="RMA status"
          style={{
            fontSize: '0.68rem', fontWeight: 700, color: colors.background,
            background: statusBadge.bg, borderRadius: '999px',
            padding: '0.15rem 0.55rem', letterSpacing: '0.06em', flexShrink: 0
          }}>
          {statusBadge.label}
        </span>
        <span style={{ color: colors.textMuted, fontSize: '0.9rem', flexShrink: 0, transition: 'transform 0.18s', transform: open ? 'rotate(180deg)' : 'none' }}>
          ▾
        </span>
      </button>

      {/* Expanded detail panel */}
      {open && (
        <div style={{
          borderTop: `1px solid ${colors.panelBorder}`,
          padding: '0.85rem 1rem 1rem',
          display: 'flex',
          flexDirection: 'column',
          gap: '0.85rem'
        }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '0.75rem' }}>
            <Row label="Contact Person" value={rma.customerName} />
            <Row label="Email" value={rma.contactEmail} />
            <Row label="Part Number" value={rma.productNumber} />
            <Row label="Issue Type" value={issueType} />
            <Row label="Submitted" value={shortDate(rma.submittedAt)} />
            {isCancelled
              ? <Row label="Closed" value={shortDate(cancellation.at)} />
              : <Row label="Archived" value={shortDate(rma.archivedAt)} />}
            {rma.assignedLocation && <Row label="Location" value={locationLabel(rma)} />}
          </div>

          {isCancelled && (cancellation.reason || cancellation.category || cancellation.stage) && (
            <div style={{
              display: 'flex', flexDirection: 'column', gap: '0.3rem',
              border: '1px solid rgba(251,146,60,0.3)', borderRadius: '8px',
              background: 'rgba(251,146,60,0.06)', padding: '0.6rem 0.75rem'
            }}>
              <span style={{ fontSize: '0.72rem', fontWeight: 700, color: '#fb923c', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Premature closure{cancellation.stage ? ` · closed at ${prettifyStage(cancellation.stage)}` : ''}
              </span>
              {cancellation.category && (
                <span style={{ fontSize: '0.8rem', color: colors.textMuted }}>
                  {CLOSURE_REASON_LABELS[cancellation.category] || cancellation.category}
                </span>
              )}
              {cancellation.reason && (
                <p style={{ margin: 0, fontSize: '0.87rem', color: colors.textBody, lineHeight: 1.55 }}>{cancellation.reason}</p>
              )}
              {cancellation.notes && (
                <p style={{ margin: 0, fontSize: '0.83rem', color: colors.textMuted, lineHeight: 1.5 }}>{cancellation.notes}</p>
              )}
              {cancellation.by && (
                <span style={{ fontSize: '0.76rem', color: colors.textMuted }}>Closed by {cancellation.by}</span>
              )}
            </div>
          )}

          {rma.description && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.15rem' }}>
              <span style={{ fontSize: '0.75rem', fontWeight: 600, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Description</span>
              <p style={{ margin: 0, fontSize: '0.87rem', color: colors.textBody, lineHeight: 1.55 }}>{rma.description}</p>
            </div>
          )}

          {rma.repairNotes && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.15rem' }}>
              <span style={{ fontSize: '0.75rem', fontWeight: 600, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Repair Notes</span>
              <p style={{ margin: 0, fontSize: '0.87rem', color: colors.textBody, lineHeight: 1.55 }}>{rma.repairNotes}</p>
            </div>
          )}

          {rma.shippingAddress?.recipientName && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.15rem' }}>
              <span style={{ fontSize: '0.75rem', fontWeight: 600, color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Shipping Address</span>
              <pre style={{ margin: 0, fontSize: '0.85rem', color: colors.textBody, fontFamily: 'inherit', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
                {formatAddress(rma.shippingAddress)}
              </pre>
            </div>
          )}

          {showPdfLink && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: '0.25rem' }}>
              <RmaPdfActions
                rmaId={rma._id || rma.rmaId}
                locale={pdfLocale}
                onLocaleChange={onChangePdfLocale}
              />
            </div>
          )}
        </div>
      )}
    </article>
  );
}

ArchivedRmaCard.propTypes = {
  rma: PropTypes.object,
  showPdfLink: PropTypes.bool,
  pdfLocale: PropTypes.string,
  onChangePdfLocale: PropTypes.func,
  highlighted: PropTypes.bool
};
